'use client';

import { PlayIcon, PauseIcon, MapPinIcon } from '@heroicons/react/24/solid';
import { useState, useRef } from 'react';

interface StoryCardProps {
  story: ApiSchema<'ResumeStoryCardData'>;
}

/**
 * @description Home Story Card Component
 * Displays a story card with video preview for the homepage horizontal carousel
 */
export const StoryCard = ({ story }: StoryCardProps) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [showIcon, setShowIcon] = useState(false);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const iconTimeout = useRef<NodeJS.Timeout | null>(null);

  const firstVideo = story.videos?.[0];
  if (!firstVideo) return null;

  const handlePlayPause = () => {
    const video = videoRef.current;
    if (!video) return;

    if (!videoLoaded) {
      video.src = firstVideo.url;
      setVideoLoaded(true);
      video.load();
    }

    if (video.paused) {
      video.play().catch(() => {});
      setIsPlaying(true);
    } else {
      video.pause();
      setIsPlaying(false);
    }

    // Show icon for 1 second
    setShowIcon(true);
    if (iconTimeout.current) clearTimeout(iconTimeout.current);
    iconTimeout.current = setTimeout(() => setShowIcon(false), 1000);
  };

  return (
    <div className="group w-full max-w-none overflow-hidden rounded-2xl bg-white shadow-lg transition-transform hover:scale-[1.02] sm:max-w-[400px]">
      {/* Video Container */}
      <div className="relative aspect-[3/4] w-full overflow-hidden bg-gray-900">
        {/* Video Element */}
        <video
          ref={videoRef}
          className="size-full object-cover"
          loop
          playsInline
          poster={firstVideo.thumbnailUrl ?? undefined}
          onClick={handlePlayPause}
        />

        {/* Play/Pause Button Overlay */}
        {!isPlaying && (
          <div
            role="presentation"
            onClick={handlePlayPause}
            className="absolute inset-0 flex cursor-pointer items-center justify-center bg-black/20"
          >
            <div className="flex size-12 items-center justify-center rounded-full bg-white/90 transition-transform hover:scale-110 sm:size-16">
              <PlayIcon className="ml-1 size-6 text-gray-900 sm:size-8" />
            </div>
          </div>
        )}

        {/* Animated Icon Feedback */}
        {showIcon && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <div className="flex size-12 items-center justify-center rounded-full bg-black/60 sm:size-16">
              {isPlaying ? (
                <PlayIcon className="size-6 animate-fade text-white sm:size-8" />
              ) : (
                <PauseIcon className="size-6 animate-fade text-white sm:size-8" />
              )}
            </div>
          </div>
        )}
      </div>

      {/* Candidate Info */}
      <div className="p-2">
        {/* Name and Title */}
        {story.leadExperience && story.privateInformation && (
          <div className="mb-2">
            <h3 className="truncate text-lg font-bold text-gray-900">
              {story.privateInformation.name}
            </h3>
            {/* Job Title - Now below the name */}
            {story.leadExperience?.title && (
              <p className="truncate text-sm font-semibold text-gray-700">
                {story.leadExperience.title}
              </p>
            )}
            <p className="truncate text-sm text-gray-600">
              @
              {' '}
              {story.leadExperience.company}
            </p>
          </div>
        )}

        {/* Location */}
        {story.location && (
          <div className="flex items-center gap-1 text-sm text-gray-500">
            <MapPinIcon className="size-3" />
            <span className="truncate">{story.location}</span>
          </div>
        )}
      </div>
    </div>
  );
};
